/**
 * LE TEXTE MIS EN FORME DES COURS : ce qu'on accepte d'écrire, et ce qu'on accepte d'afficher.
 *
 * ──────────────────────────────────────────────────────────────────────────────────────────
 * POURQUOI UNE LISTE FERMÉE PLUTÔT QU'UN NETTOYAGE APRÈS COUP. Le coach écrit, l'apprenant
 * lit : tout ce qui est stocké ici finit par être inséré dans la page de quelqu'un d'autre.
 * Un `dangerouslySetInnerHTML` sur du HTML arbitraire, c'est une porte ouverte — un `<script>`,
 * un `<img onerror=…>`, un `<a href="javascript:…">` s'exécutent chez l'apprenant.
 *
 * On ne retire donc pas « ce qui est dangereux » (liste sans fin, qu'on oublie de tenir à
 * jour) : on ne GARDE que ce qu'on sait afficher. Tout le reste disparaît, attributs compris.
 * Une balise inconnue perd son enveloppe mais garde son texte — on ne fait jamais disparaître
 * ce que le coach a écrit.
 *
 * LES ATTRIBUTS SONT TOUS SUPPRIMÉS, À UNE EXCEPTION PRÈS. Ni `class`, ni `href`, ni `on*` :
 * la mise en forme vient de la feuille de style de l'écran qui affiche, pas du texte collé.
 * Un copier-coller depuis Word, qui charrie des dizaines d'attributs et des polices en dur,
 * ressort ainsi propre et lisible.
 *
 * L'exception est `style`, et uniquement pour la couleur, le surlignage et l'alignement —
 * il n'existe pas de balise HTML « rouge ». Il n'est pas analysé mais COMPARÉ à une liste
 * fermée de déclarations (voir `STYLES_AUTORISES`) : une valeur qui n'y figure pas au
 * caractère près est retirée.
 *
 * `DOMParser` plutôt qu'une expression régulière : on ne découpe pas du HTML au motif. Un
 * `<script>` écrit sur deux lignes, un attribut sans guillemets, une balise non fermée — la
 * régulière passe à côté, l'analyseur du navigateur non.
 * ──────────────────────────────────────────────────────────────────────────────────────────
 */

/** Les seules balises qu'un coach peut produire, et les seules qu'un apprenant verra. */
const BALISES_AUTORISEES = new Set([
  "B", "STRONG",        // gras
  "I", "EM",            // italique
  "U",                  // souligné
  "S", "STRIKE", "DEL", // barré
  "UL", "OL", "LI",     // listes
  "P", "BR", "DIV",     // paragraphes et retours
  "H3", "H4",           // sous-titres dans une section
  "SPAN",               // porteur de couleur et de surlignage, et rien d'autre
  "MARK",               // surlignage, quand le navigateur l'écrit ainsi
  "SUB", "SUP",         // indice et exposant
  "BLOCKQUOTE",         // exemple ou citation mis en retrait
]);

/* ── LA COULEUR, SEULE EXCEPTION À « AUCUN ATTRIBUT » ──────────────────────────────────
   Mettre un mot en rouge demande forcément un attribut : il n'existe pas de balise HTML
   « rouge ». On ouvre donc `style`, mais d'une largeur de porte très précise.

   ON NE VALIDE PAS LA SYNTAXE, ON COMPARE À UNE LISTE. Accepter « toute couleur CSS »
   obligerait à écrire un analyseur, et c'est exactement là que les failles se logent :
   `background:url(javascript:…)`, `expression(…)`, un `/*` qui commente la fin de la règle.
   Ici, une déclaration qui n'est pas au caractère près dans `STYLES_AUTORISES` n'existe pas.

   La palette est donc fermée des DEUX côtés : la barre d'outils ne propose que ces
   couleurs, et l'affichage n'accepte que celles-là. */
export const COULEURS_TEXTE = [
  { nom: "Noir",   valeur: "#111827" },
  { nom: "Rouge",  valeur: "#dc2626" },
  { nom: "Orange", valeur: "#ea580c" },
  { nom: "Vert",   valeur: "#059669" },
  { nom: "Bleu",   valeur: "#2563eb" },
  { nom: "Violet", valeur: "#7c3aed" },
  { nom: "Gris",   valeur: "#6b7280" },
];

export const COULEURS_SURLIGNAGE = [
  { nom: "Jaune", valeur: "#fef08a" },
  { nom: "Vert",  valeur: "#bbf7d0" },
  { nom: "Bleu",  valeur: "#bfdbfe" },
  { nom: "Rose",  valeur: "#fbcfe8" },
];

const ALIGNEMENTS = ["left", "center", "right", "justify"];

/** L'ensemble exact des déclarations CSS acceptées, comparées caractère par caractère. */
const STYLES_AUTORISES = new Set([
  ...COULEURS_TEXTE.map(c => `color: ${c.valeur}`),
  ...COULEURS_SURLIGNAGE.map(c => `background-color: ${c.valeur}`),
  ...ALIGNEMENTS.map(a => `text-align: ${a}`),
]);

/**
 * Ne garde d'un attribut `style` que les déclarations de la liste.
 * Rend une chaîne vide si rien ne survit — l'attribut est alors retiré.
 */
function filtrerStyle(valeur) {
  return String(valeur || "")
    .split(";")
    .map(d => normaliser(d))
    .filter(d => STYLES_AUTORISES.has(d))
    .join("; ");
}

/**
 * Ramène une déclaration à la forme exacte de la liste.
 *
 * `execCommand("foreColor", "#dc2626")` n'écrit PAS l'hexadécimal qu'on lui donne : les
 * navigateurs rendent `color: rgb(220, 38, 38)`. Sans cette conversion, la couleur qu'on
 * vient d'appliquer serait retirée par le nettoyage une milliseconde plus tard — le bouton
 * paraîtrait simplement ne rien faire.
 */
function normaliser(declaration) {
  const d = String(declaration || "").trim().toLowerCase().replace(/\s*:\s*/, ": ");
  return d.replace(/rgba?\(([^)]+)\)/, (tout, nombres) => {
    const [r, v, b] = nombres.split(",").map(n => parseInt(n.trim(), 10));
    if ([r, v, b].some(n => Number.isNaN(n))) return tout;
    const hex = (n) => n.toString(16).padStart(2, "0");
    return `#${hex(r)}${hex(v)}${hex(b)}`;
  });
}

/* ── LA MISE EN FORME ÉCRITE EN CSS PLUTÔT QU'EN BALISE ────────────────────────────────
   Selon le navigateur et son réglage `styleWithCSS`, « gras » s'écrit `<b>` OU
   `<span style="font-weight: bold">`. La seconde forme n'est pas dans la liste des styles
   autorisés : elle était donc retirée, et le gras disparaissait sans trace.

   Plutôt que d'élargir la liste des styles — ce qui ferait porter la mise en forme par des
   attributs qu'il faudrait ensuite valider un à un — on RETRADUIT ces déclarations vers la
   balise correspondante. Un seul vocabulaire en sortie, quelle que soit l'humeur du
   navigateur en entrée. */
const CSS_VERS_BALISE = [
  { motif: /font-weight:\s*(bold|[6-9]00)/,        balise: "b" },
  { motif: /font-style:\s*italic/,                 balise: "i" },
  { motif: /text-decoration[^;]*underline/,        balise: "u" },
  { motif: /text-decoration[^;]*line-through/,     balise: "s" },
  { motif: /vertical-align:\s*super/,              balise: "sup" },
  { motif: /vertical-align:\s*sub/,                balise: "sub" },
];

/** Enveloppe le contenu d'un élément dans les balises que son `style` décrit. */
function retraduireStyle(el, styleBrut) {
  const css = String(styleBrut || "").toLowerCase();
  const balises = CSS_VERS_BALISE.filter(r => r.motif.test(css)).map(r => r.balise);
  if (!balises.length) return el;
  const doc = el.ownerDocument;
  let interieur = el;
  for (const nom of balises) {
    const enveloppe = doc.createElement(nom);
    while (interieur.firstChild) enveloppe.appendChild(interieur.firstChild);
    interieur.appendChild(enveloppe);
    interieur = enveloppe;
  }
  return el;
}

/** Ces balises n'ont pas de contenu lisible : on ne garde même pas leur texte. */
const A_SUPPRIMER = new Set(["SCRIPT", "STYLE", "IFRAME", "OBJECT", "EMBED", "LINK", "META"]);

/**
 * Rend une version sûre du HTML reçu : seules les balises de la liste subsistent, sans
 * aucun attribut. Rend une chaîne vide si le navigateur n'est pas disponible.
 */
export function nettoyerHtml(html) {
  if (!html || typeof html !== "string") return "";
  if (typeof window === "undefined" || !window.DOMParser) return "";

  const doc = new window.DOMParser().parseFromString(`<body>${html}</body>`, "text/html");

  const parcourir = (noeud) => {
    // On copie la liste : on va modifier les enfants pendant qu'on les parcourt.
    for (const enfant of Array.from(noeud.childNodes)) {
      if (enfant.nodeType === 3) continue;              // texte : rien à faire
      if (enfant.nodeType !== 1) { enfant.remove(); continue; } // commentaire, etc.

      const balise = enfant.tagName.toUpperCase();

      if (A_SUPPRIMER.has(balise)) { enfant.remove(); continue; }

      /* ── LE CAS `<font>`, ET C'EST LUI QUI CASSAIT LA COULEUR ──────────────────────
         `execCommand("foreColor")` n'écrit pas un `<span style>` : par défaut, Chrome et
         Safari produisent `<font color="#dc2626">`. Cette balise ne figurait pas dans la
         liste, elle était donc dépliée et la couleur perdue — tandis qu'un `<b>` posé
         par-dessus survivait. D'où le symptôme : le gras tenait, la couleur non.
         On la convertit en `span` portant un style validé, au lieu de la jeter. */
      if (balise === "FONT") {
        const couleur = enfant.getAttribute("color");
        const depuisAttribut = couleur ? normaliser(`color: ${couleur}`) : "";
        const styleFont = [filtrerStyle(enfant.getAttribute("style")),
                           STYLES_AUTORISES.has(depuisAttribut) ? depuisAttribut : ""]
                          .filter(Boolean).join("; ");
        const span = enfant.ownerDocument.createElement("span");
        if (styleFont) span.setAttribute("style", styleFont);
        while (enfant.firstChild) span.appendChild(enfant.firstChild);
        enfant.replaceWith(span);
        // On repasse dessus comme sur n'importe quel span : il sera déplié s'il est nu.
        if (!styleFont) {
          parcourir(span);
          span.replaceWith(...Array.from(span.childNodes));
        } else {
          parcourir(span);
        }
        continue;
      }

      if (!BALISES_AUTORISEES.has(balise)) {
        /* Balise inconnue : on la remplace par son contenu. Le texte du coach survit,
           son enveloppe non. */
        parcourir(enfant);
        enfant.replaceWith(...Array.from(enfant.childNodes));
        continue;
      }

      /* Balise acceptée : on la vide de tous ses attributs, à la seule exception d'un
         `style` réduit aux déclarations de la liste fermée. Avant de le jeter, on en
         récupère la mise en forme qu'il décrivait (gras, italique…) sous forme de balises. */
      const styleBrut = enfant.getAttribute("style");
      retraduireStyle(enfant, styleBrut);
      const style = filtrerStyle(styleBrut);
      for (const attr of Array.from(enfant.attributes)) enfant.removeAttribute(attr.name);
      if (style) enfant.setAttribute("style", style);

      /* Un `span` qui n'a plus de style ne sert plus à rien : il ne portait que ça. On le
         remplace par son contenu, sinon chaque passage de l'éditeur en empilerait un de
         plus et le HTML enflerait sans jamais rien changer à l'écran. */
      if (balise === "SPAN" && !style) {
        parcourir(enfant);
        enfant.replaceWith(...Array.from(enfant.childNodes));
        continue;
      }

      parcourir(enfant);
    }
  };

  parcourir(doc.body);
  return doc.body.innerHTML;
}

/**
 * Le texte nu d'un contenu mis en forme.
 *
 * Sert à deux choses : garder `contenu` lisible pour tout ce qui ne sait pas afficher du
 * HTML (exports, recherche, anciens écrans), et mesurer si une section est vraiment vide —
 * `<p><br></p>` n'est pas du contenu, c'est un éditeur qui vient d'être ouvert.
 */
export function htmlVersTexte(html) {
  if (!html || typeof html !== "string") return "";
  if (typeof window === "undefined" || !window.DOMParser) return "";
  const doc = new window.DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
  // Les sauts de ligne doivent survivre à la conversion, sinon deux paragraphes se collent.
  doc.body.querySelectorAll("br").forEach(br => br.replaceWith("\n"));
  doc.body.querySelectorAll("p, div, li, h3, h4, blockquote").forEach(el => el.append("\n"));
  return (doc.body.textContent || "").replace(/\n{3,}/g, "\n\n").trim();
}

/** Vrai quand le HTML ne porte aucun texte — un éditeur ouvert et laissé vide. */
export const htmlEstVide = (html) => htmlVersTexte(html).length === 0;
